/**
 * Générateur — Utiliser des modèles pré-algébriques pour résoudre des problèmes algébriques
 * Attendu ID : 637 | Niveau : 6ème | Domaine : A (Algèbre)
 *
 * Ce générateur contient 3 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI
 * pour un total de 9 entrées registry.
 *
 * Exercices couverts (voir FICHE_PEDAGO_CLUSTER20) :
 *
 *   1. `balance_inconnue`      → QCM, Saisie, Texte à trous.
 *      Balance en équilibre : trouver la masse d'un objet inconnu.
 *
 *   2. `repartition_ecarts`    → QCM, Saisie, Texte à trous.
 *      Répartir un total entre 2 ou 3 parts avec écarts connus.
 *
 *   3. `substitution_prix`     → QCM, Saisie, Relier.
 *      Retrouver des prix individuels à partir de combinaisons.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 637;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_BALANCE   = 'Trouver la masse d\'un objet inconnu à l\'aide d\'une balance';
const L_REPART    = 'Répartir un total connaissant les écarts entre les parts';
const L_SUBSTIT   = 'Retrouver des prix individuels à partir de combinaisons';

// 13 prénoms fixes
const PRENOMS = [
    { nom: 'Adam', fem: false }, { nom: 'Amélie', fem: true },
    { nom: 'Chloé', fem: true }, { nom: 'Hugo', fem: false },
    { nom: 'Inès', fem: true }, { nom: 'Jade', fem: true },
    { nom: 'Karim', fem: false }, { nom: 'Léo', fem: false },
    { nom: 'Lucas', fem: false }, { nom: 'Manon', fem: true },
    { nom: 'Sofia', fem: true }, { nom: 'Thomas', fem: false },
    { nom: 'Yanis', fem: false },
];

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/** Ajoute un distracteur s'il est unique et différent de la bonne réponse. */
const addUnique = (set, val, correct) => {
    if (val !== correct && val !== '') set.add(val);
};

/** Tire 2 prénoms distincts. */
const deuxPrenoms = () => {
    const s = shuffle(PRENOMS);
    return [s[0], s[1]];
};

/** Majuscule initiale. */
const ucFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Contraction « de le » → « du », « de les » → « des », « de la » intact. */
const dePart = (beneficiaire) => {
    if (beneficiaire.startsWith('le ')) return `du ${beneficiaire.slice(3)}`;
    if (beneficiaire.startsWith('les ')) return `des ${beneficiaire.slice(4)}`;
    return `de ${beneficiaire}`;
};

/** Tire 3 prénoms distincts. */
const troisPrenoms = () => {
    const s = shuffle(PRENOMS);
    return [s[0], s[1], s[2]];
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — balance_inconnue
// ═══════════════════════════════════════════════════════════

// L'unité est portée par l'objet (revue 2026-07-31) : masses 2–140 environ,
// plausibles en kg pour les gros contenants, en g pour les petits.
const OBJETS_BALANCE = [
    { nom: 'sac', article: 'un sac', articlePluriel: 'sacs', genre: 'm', unite: 'kg' },
    { nom: 'colis', article: 'un colis', articlePluriel: 'colis', genre: 'm', unite: 'kg' },
    { nom: 'bidon', article: 'un bidon', articlePluriel: 'bidons', genre: 'm', unite: 'kg' },
    { nom: 'caisse', article: 'une caisse', articlePluriel: 'caisses', genre: 'f', unite: 'kg' },
    { nom: 'boîte', article: 'une boîte', articlePluriel: 'boîtes', genre: 'f', unite: 'g' },
    { nom: 'pot', article: 'un pot', articlePluriel: 'pots', genre: 'm', unite: 'g' },
    { nom: 'tube', article: 'un tube', articlePluriel: 'tubes', genre: 'm', unite: 'g' },
    { nom: 'flacon', article: 'un flacon', articlePluriel: 'flacons', genre: 'm', unite: 'g' },
    { nom: 'sachet', article: 'un sachet', articlePluriel: 'sachets', genre: 'm', unite: 'g' },
];

/**
 * Génère un problème de balance (type A : 1 objet + masse connue = total,
 * ou type B : n objets identiques = total).
 * Retourne { n, masseConnue, masseTotal, reponse, objet, unite }.
 */
const genBalanceData = () => {
    const objet = randomChoice(OBJETS_BALANCE);
    const unite = objet.unite;
    const typeB = Math.random() < 0.5;

    if (typeB) {
        // Type B : n objets identiques (+ éventuellement masse connue) = total
        const n = randInt(2, 5);
        const masseObjet = randInt(3, 25);
        const hasMasseConnue = Math.random() < 0.4;
        const masseConnue = hasMasseConnue ? randInt(1, 15) : 0;
        const masseTotal = n * masseObjet + masseConnue;
        return { n, masseConnue, masseTotal, reponse: masseObjet, objet, unite };
    }
    // Type A : 1 objet + masse connue = total
    const masseTotal = randInt(8, 50);
    const masseConnue = randInt(1, masseTotal - 2);
    const reponse = masseTotal - masseConnue;
    return { n: 1, masseConnue, masseTotal, reponse, objet, unite };
};

/** Construit le SVG de la balance.
 *  CONTRAINTE PRINT : cette image est aussi imprimée, souvent en demi-colonne
 *  (~85 mm). Le viewBox colle au dessin (avant : 680×310 dont un tiers vide →
 *  l'échelle de rendu écrasait tout) et les textes — qui portent TOUTE
 *  l'information de l'exercice — restent ≥ 14px. */
const buildBalanceSvg = (data) => {
    const { n, masseConnue, masseTotal, unite, objet } = data;
    const nb = Math.min(n, 6);
    const boxW = 28, boxH = 30, boxGap = 4;

    // Géométrie paramétrique : coupelles de 200px centrées en 116 et 444,
    // fléau à y=118, socle en bas → viewBox 560×218 sans bande vide.
    const panW = 200;
    const pxL = 16, pxR = 344;                          // bords gauches des coupelles
    const ccL = pxL + panW / 2, ccR = pxR + panW / 2;   // centres (= attaches du fléau)
    const panY = 66;                                    // haut de la lèvre des coupelles
    const beamY = 118;
    const cx = 280;                                     // axe du pivot

    // Lèvre incurvée + plateau d'une coupelle
    const coupelle = (px) =>
        `<path d="M${px} ${panY + 12} Q${px + 5} ${panY} ${px + 15} ${panY} L${px + panW - 15} ${panY} Q${px + panW - 5} ${panY} ${px + panW} ${panY + 12} L${px + panW} ${panY + 14} Q${px + panW - 5} ${panY + 10} ${px + panW - 15} ${panY + 10} L${px + 15} ${panY + 10} Q${px + 5} ${panY + 10} ${px} ${panY + 14} Z" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.5"/>` +
        `<rect x="${px}" y="${panY + 12}" width="${panW}" height="8" rx="2" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.5" opacity="0.5"/>`;

    // Boîtes "?" sur le plateau gauche
    const totalBoxW = nb * boxW + (nb - 1) * boxGap;
    const masseConnueW = masseConnue > 0 ? 56 : 0;
    const totalGaucheW = totalBoxW + (masseConnue > 0 ? 8 + masseConnueW : 0);
    const boxStartX = ccL - totalGaucheW / 2;
    const boxY = panY - 32; // posées sur la coupelle

    let objetsGauche = '';
    for (let i = 0; i < nb; i++) {
        const x = boxStartX + i * (boxW + boxGap);
        objetsGauche += `<rect x="${x}" y="${boxY}" width="${boxW}" height="${boxH}" rx="4" fill="#f59e0b" stroke="#d97706" stroke-width="0.8"/>`;
        objetsGauche += `<text x="${x + boxW / 2}" y="${boxY + 21}" font-size="16" font-weight="600" fill="#78350f" text-anchor="middle" font-family="system-ui,sans-serif">?</text>`;
    }
    // Masse connue (bloc gris) à côté des boîtes
    if (masseConnue > 0) {
        const mx = boxStartX + totalBoxW + 8;
        objetsGauche += `<rect x="${mx}" y="${panY - 28}" width="${masseConnueW}" height="26" rx="4" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.8" opacity="0.5"/>`;
        objetsGauche += `<text x="${mx + masseConnueW / 2}" y="${panY - 9}" font-size="14" font-weight="600" fill="currentColor" text-anchor="middle" font-family="system-ui,sans-serif">${masseConnue} ${unite}</text>`;
    }
    // Label objets (aligné sur l'objet de l'énoncé)
    const labelBoites = n > 1 ? `${n} ${objet.articlePluriel} identiques` : `1 ${objet.nom}`;

    const svg = `<svg viewBox="0 0 560 218" xmlns="http://www.w3.org/2000/svg">
  <!-- Socle -->
  <rect x="${cx - 30}" y="168" width="60" height="40" rx="4" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.5" opacity="0.6"/>
  <rect x="${cx - 60}" y="156" width="120" height="16" rx="3" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.5" opacity="0.6"/>
  <!-- Pivot -->
  <polygon points="${cx},156 ${cx - 8},128 ${cx + 8},128" fill="var(--svg-fg-dim, #94a3b8)" opacity="0.7"/>
  <circle cx="${cx}" cy="126" r="5" fill="var(--svg-fg-dim, #94a3b8)" stroke="currentColor" stroke-width="0.5"/>
  <!-- Fléau -->
  <line x1="${ccL}" y1="${beamY}" x2="${ccR}" y2="${beamY}" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
  <!-- Chaînes -->
  <line x1="${ccL}" y1="${beamY}" x2="${ccL}" y2="${panY + 12}" stroke="currentColor" stroke-width="1.2"/>
  <line x1="${ccR}" y1="${beamY}" x2="${ccR}" y2="${panY + 12}" stroke="currentColor" stroke-width="1.2"/>
  <line x1="${ccL - 5}" y1="${panY + 12}" x2="${ccL + 5}" y2="${panY + 12}" stroke="currentColor" stroke-width="1.2"/>
  <line x1="${ccR - 5}" y1="${panY + 12}" x2="${ccR + 5}" y2="${panY + 12}" stroke="currentColor" stroke-width="1.2"/>
  <!-- Coupelles -->
  ${coupelle(pxL)}
  ${coupelle(pxR)}
  <!-- Boîtes sur plateau gauche -->
  ${objetsGauche}
  <!-- Label boîtes -->
  <text x="${ccL}" y="24" font-size="16" fill="currentColor" text-anchor="middle" font-family="system-ui,sans-serif">${labelBoites}</text>
  <!-- Masse connue (plateau droit) -->
  <rect x="${ccR - 43}" y="${panY - 46}" width="86" height="44" rx="6" fill="#dbeafe" stroke="#93c5fd" stroke-width="0.8"/>
  <text x="${ccR}" y="${panY - 18}" font-size="18" font-weight="600" fill="#1e40af" text-anchor="middle" font-family="system-ui,sans-serif">${masseTotal} ${unite}</text>
  <!-- Indicateur d'équilibre -->
  <path d="M${cx - 14} ${beamY - 4} L${cx} ${beamY + 5} L${cx + 14} ${beamY - 4}" fill="none" stroke="#f59e0b" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/** Construit l'énoncé textuel de la balance. */
const buildBalanceTexte = (data) => {
    const { n, masseConnue, masseTotal, objet, unite } = data;
    const un = objet.genre === 'f' ? 'une' : 'un';
    if (n === 1 && masseConnue > 0) {
        return `<p>Une balance est en équilibre. D'un côté, il y a ${un} ${objet.nom} et une masse de ${masseConnue} ${unite}. De l'autre côté, une masse de ${masseTotal} ${unite}.</p>`;
    }
    if (n > 1 && masseConnue > 0) {
        return `<p>Une balance est en équilibre. D'un côté, il y a ${n} ${objet.articlePluriel} identiques et une masse de ${masseConnue} ${unite}. De l'autre côté, une masse de ${masseTotal} ${unite}.</p>`;
    }
    if (n > 1 && masseConnue === 0) {
        return `<p>Une balance est en équilibre. D'un côté, il y a ${n} ${objet.articlePluriel} identiques. De l'autre côté, une masse de ${masseTotal} ${unite}.</p>`;
    }
    // n=1, masseConnue=0 — trivial, éviter
    return `<p>Une balance est en équilibre. D'un côté, il y a ${un} ${objet.nom}. De l'autre côté, une masse de ${masseTotal} ${unite}.</p>`;
};

// --- QCM ---
const genBalanceQcm = () => {
    const data = genBalanceData();
    const { n, masseConnue, masseTotal, reponse, objet, unite } = data;
    const correct = `${reponse} ${unite}`;

    const dSet = new Set();
    addUnique(dSet, `${masseTotal} ${unite}`, correct);                    // total
    if (masseConnue > 0) addUnique(dSet, `${masseConnue} ${unite}`, correct); // masse connue
    addUnique(dSet, `${masseTotal + masseConnue} ${unite}`, correct);      // addition
    if (n > 1) {
        addUnique(dSet, `${masseTotal - masseConnue} ${unite}`, correct);  // oubli division
        addUnique(dSet, `${reponse * n} ${unite}`, correct);              // multiplication
    }
    addUnique(dSet, `${reponse + 1} ${unite}`, correct);
    addUnique(dSet, `${Math.max(1, reponse - 1)} ${unite}`, correct);
    // Fallback
    let att = 0;
    while (dSet.size < 3 && att < 20) {
        addUnique(dSet, `${randInt(1, 50)} ${unite}`, correct);
        att++;
    }

    const choixList = [correct, ...[...dSet].slice(0, 3)];
    const svgUrl = buildBalanceSvg(data);
    const enonce = buildBalanceTexte(data);
    const un = objet.genre === 'f' ? 'une' : 'un';
    const texte = `${enonce}{{element:0}}<p>Combien pèse ${un} ${objet.nom}\u00a0?</p>`;
    const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };

    const payload = makeQcm(choixList, correct, texte, { ...opts(L_BALANCE), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'balance_inconnue';
    return payload;
};

// --- Saisie ---
const genBalanceSaisie = () => {
    const data = genBalanceData();
    const { reponse, objet, unite } = data;
    const svgUrl = buildBalanceSvg(data);
    const enonce = buildBalanceTexte(data);
    const un = objet.genre === 'f' ? 'une' : 'un';
    const texte = `${enonce}{{element:0}}<p>Combien pèse ${un} ${objet.nom}\u00a0?</p>`;
    const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };

    const payload = makeSaisie(String(reponse), texte, { ...opts(L_BALANCE), suffixe: unite, elements_additionnels: elAdd });
    payload._meta.generatorKey = 'balance_inconnue';
    return payload;
};

// --- Texte à trous ---
const genBalanceTexteTrou = () => {
    const data = genBalanceData();
    const { n, masseConnue, masseTotal, reponse, objet, unite } = data;
    const svgUrl = buildBalanceSvg(data);
    const enonce = buildBalanceTexte(data);

    let phrase;
    if (n === 1) {
        phrase = `Le total sur le plateau de droite est {{${masseTotal}}} ${unite}. La masse connue sur le plateau de gauche est ${masseConnue} ${unite}. Donc la masse ${objet.genre === 'f' ? 'de la' : 'du'} ${objet.nom} est ${masseTotal} − ${masseConnue} = {{${reponse}}} ${unite}.`;
    } else if (masseConnue === 0) {
        phrase = `Le total est {{${masseTotal}}} ${unite}. Il y a {{${n}}} ${objet.articlePluriel} identiques. Chaque ${objet.nom} pèse ${masseTotal} ÷ ${n} = {{${reponse}}} ${unite}.`;
    } else {
        const diff = masseTotal - masseConnue;
        phrase = `Le total est ${masseTotal} ${unite}. On retire la masse connue de ${masseConnue} ${unite} : ${masseTotal} − ${masseConnue} = {{${diff}}} ${unite}. On divise par ${n} : ${diff} ÷ ${n} = {{${reponse}}} ${unite}.`;
    }

    const texte = `${enonce}{{element:0}}<p>Complète le raisonnement pour trouver la masse d'${objet.genre === 'f' ? 'une' : 'un'} ${objet.nom}.</p>`;
    const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_BALANCE), mode: 'saisie', strict: false, elements_additionnels: elAdd });
    payload._meta.generatorKey = 'balance_inconnue';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — repartition_ecarts
// ═══════════════════════════════════════════════════════════

/**
 * Contextes de répartition à 3 parts.
 * Chaque bénéficiaire porte son article défini (le/la/l') pour une grammaire correcte.
 */
const CTX_REPART = [
    { theme: 'course cycliste', beneficiaires: ['le premier coureur', 'le deuxième coureur', 'le troisième coureur'], suffixe: '€', intro: (p, total) => `${p.nom} organise une course cycliste. Une prime totale de ${total} € sera répartie entre les trois premiers coureurs.` },
    { theme: 'partage de bonbons', beneficiaires: ['le plus grand', 'le moyen', 'le plus petit'], suffixe: 'bonbons', intro: (p, total) => `${p.nom} partage ${total} bonbons entre trois enfants.` },
    { theme: 'partage de billes', beneficiaires: ['le premier ami', 'le deuxième ami', 'le troisième ami'], suffixe: 'billes', intro: (p, total) => `${p.nom} répartit ${total} billes entre trois amis.` },
    { theme: 'distribution de livres', beneficiaires: ['la première classe', 'la deuxième classe', 'la troisième classe'], suffixe: 'livres', intro: (p, total) => `${p.nom} distribue ${total} livres entre trois classes.` },
    { theme: 'répartition argent', beneficiaires: ['le premier enfant', 'le deuxième enfant', 'le troisième enfant'], suffixe: '€', intro: (p, total) => `${p.nom} répartit ${total} € entre ses trois enfants.` },
];

/**
 * Génère des données de répartition valides à 3 parts.
 * base = (total - ecart1 + ecart2) / 3, entier, base > 0, toutes parts > 0.
 */
const genRepartData3 = () => {
    const ctx = randomChoice(CTX_REPART);
    const prenom = randomChoice(PRENOMS);
    let total, ecart1, ecart2, base;
    let attempts = 0;
    do {
        total = randomChoice([120, 150, 180, 200, 240, 270, 300, 320, 360, 400, 450, 500]);
        ecart1 = randInt(1, 8) * 10;  // 10 à 80
        ecart2 = randInt(1, 8) * 10;
        base = (total - ecart1 + ecart2) / 3;
        attempts++;
    } while ((base !== Math.floor(base) || base < 10 || base - ecart2 < 1) && attempts < 200);

    const part1 = base + ecart1;
    const part2 = base;
    const part3 = base - ecart2;
    return { ctx, prenom, total, ecart1, ecart2, base, part1, part2, part3 };
};

/**
 * Génère des données de répartition à 2 parts.
 */
const genRepartData2 = () => {
    const prenom = randomChoice(PRENOMS);
    const total = randomChoice([30, 40, 50, 60, 80, 100, 120, 150, 200]);
    const ecart = randInt(1, 6) * 2; // pair, 2 à 12
    const petite = (total - ecart) / 2;
    const grande = petite + ecart;
    return { prenom, total, ecart, petite, grande };
};

// --- QCM ---
const genRepartQcm = () => {
    const data = genRepartData3();
    const { ctx, prenom, total, ecart1, ecart2, base, part1, part2, part3 } = data;
    // Demander une part au hasard
    const partIdx = randInt(0, 2);
    const parts = [part1, part2, part3];
    const reponse = parts[partIdx];
    const correct = `${reponse} ${ctx.suffixe}`;

    const dSet = new Set();
    addUnique(dSet, `${Math.round(total / 3)} ${ctx.suffixe}`, correct); // partage équitable
    addUnique(dSet, `${total - ecart1 - ecart2} ${ctx.suffixe}`, correct); // soustraction brute
    // Distracteur proche tiré à ± 1 au hasard : avec +1 systématique, la bonne
    // réponse n'était JAMAIS le max des choix (biais « cocher le plus petit »).
    const proche = Math.random() < 0.5 ? reponse + 1 : Math.max(1, reponse - 1);
    addUnique(dSet, `${proche} ${ctx.suffixe}`, correct);
    addUnique(dSet, `${ecart1} ${ctx.suffixe}`, correct);
    addUnique(dSet, `${total - base} ${ctx.suffixe}`, correct);
    let att = 0;
    while (dSet.size < 3 && att < 20) {
        addUnique(dSet, `${randInt(10, Math.max(total - 10, 20))} ${ctx.suffixe}`, correct);
        att++;
    }
    const choixList = [correct, ...[...dSet].slice(0, 3)];

    const texte = `<p>${ctx.intro(prenom, total)} ${ucFirst(ctx.beneficiaires[0])} reçoit ${ecart1} ${ctx.suffixe} de plus que ${ctx.beneficiaires[1]}. ${ucFirst(ctx.beneficiaires[2])} reçoit ${ecart2} ${ctx.suffixe} de moins que ${ctx.beneficiaires[1]}. Combien reçoit ${ctx.beneficiaires[partIdx]} ?</p>`;

    const payload = makeQcm(choixList, correct, texte, opts(L_REPART));
    payload._meta.generatorKey = 'repartition_ecarts';
    return payload;
};

// --- Saisie ---
const genRepartSaisie = () => {
    // Variante à 2 parts pour la saisie (plus accessible)
    const data = genRepartData2();
    const { total, ecart, petite, grande } = data;
    const [pShare, p1, p2] = troisPrenoms(); // 3 prénoms distincts
    const demandePetite = Math.random() < 0.5;
    const reponse = demandePetite ? petite : grande;
    const qui = demandePetite ? p2.nom : p1.nom;
    const texte = `<p>${pShare.nom} partage ${total} billes entre ${p1.nom} et ${p2.nom}. ${p1.nom} reçoit ${ecart} billes de plus que ${p2.nom}. Combien de billes reçoit ${qui} ?</p>`;

    const payload = makeSaisie(String(reponse), texte, { ...opts(L_REPART), suffixe: 'billes' });
    payload._meta.generatorKey = 'repartition_ecarts';
    return payload;
};

// --- Texte à trous ---
const genRepartTexteTrou = () => {
    // Force ecart1 >= ecart2 pour éviter les différences négatives dans la phrase
    let data;
    let attempts = 0;
    do {
        data = genRepartData3();
        attempts++;
    } while (data.ecart1 < data.ecart2 && attempts < 50);
    // Si après 50 essais on a encore ecart1 < ecart2, on échange
    if (data.ecart1 < data.ecart2) {
        const tmp = data.ecart1;
        data.ecart1 = data.ecart2;
        data.ecart2 = tmp;
        data.base = (data.total - data.ecart1 + data.ecart2) / 3;
        data.part1 = data.base + data.ecart1;
        data.part2 = data.base;
        data.part3 = data.base - data.ecart2;
    }
    const { ctx, prenom, total, ecart1, ecart2, base, part1, part2, part3 } = data;
    const diff = ecart1 - ecart2; // toujours >= 0

    const diffPart = diff === 0
        ? `La somme des trois parts vaut 3 × base.\nDonc 3 × base = {{${total}}}.\nBase = {{${base}}}.`
        : `La somme des trois parts vaut 3 × base + ${ecart1} − ${ecart2} = 3 × base + {{${diff}}}.\nDonc 3 × base = ${total} − ${diff} = {{${total - diff}}}.\nBase = {{${base}}}.`;

    const phrase = `On appelle la part ${dePart(ctx.beneficiaires[1])} : base.\n${ucFirst(ctx.beneficiaires[0])} reçoit base + ${ecart1} et ${ctx.beneficiaires[2]} reçoit base − ${ecart2}.\n${diffPart}\n${ucFirst(ctx.beneficiaires[0])} reçoit {{${part1}}} ${ctx.suffixe}, ${ctx.beneficiaires[1]} reçoit {{${part2}}} ${ctx.suffixe}, ${ctx.beneficiaires[2]} reçoit {{${part3}}} ${ctx.suffixe}.`;

    const texte = `<p>${ctx.intro(prenom, total)} ${ucFirst(ctx.beneficiaires[0])} reçoit ${ecart1} ${ctx.suffixe} de plus que ${ctx.beneficiaires[1]}. ${ucFirst(ctx.beneficiaires[2])} reçoit ${ecart2} ${ctx.suffixe} de moins que ${ctx.beneficiaires[1]}. Complète le raisonnement.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_REPART), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'repartition_ecarts';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — substitution_prix
// ═══════════════════════════════════════════════════════════

// Plages de prix par objet (revue 2026-08-01) : avant, prixA ∈ [2..15] et
// prixB ∈ [1..12] quel que soit l'objet → « un crayon à 15 € », « un bonbon
// à 12 € » dans 30 % des tirages.
const CTX_SUBSTIT = [
    { objA: 'pastèque', objB: 'ananas', articleA: 'une pastèque', articleB: 'un ananas', plurA: 'pastèques', plurB: 'ananas', genreA: 'f', genreB: 'm', prixARange: [3, 8], prixBRange: [2, 6] },
    { objA: 'livre', objB: 'cahier', articleA: 'un livre', articleB: 'un cahier', plurA: 'livres', plurB: 'cahiers', genreA: 'm', genreB: 'm', prixARange: [5, 15], prixBRange: [1, 5] },
    { objA: 'pizza', objB: 'boisson', articleA: 'une pizza', articleB: 'une boisson', plurA: 'pizzas', plurB: 'boissons', genreA: 'f', genreB: 'f', prixARange: [6, 14], prixBRange: [1, 4] },
    { objA: 'crayon', objB: 'gomme', articleA: 'un crayon', articleB: 'une gomme', plurA: 'crayons', plurB: 'gommes', genreA: 'm', genreB: 'f', prixARange: [1, 4], prixBRange: [1, 3] },
    { objA: 'gâteau', objB: 'bonbon', articleA: 'un gâteau', articleB: 'un bonbon', plurA: 'gâteaux', plurB: 'bonbons', genreA: 'm', genreB: 'm', prixARange: [2, 8], prixBRange: [1, 2] },
    { objA: 'ballon', objB: 'raquette', articleA: 'un ballon', articleB: 'une raquette', plurA: 'ballons', plurB: 'raquettes', genreA: 'm', genreB: 'f', prixARange: [4, 12], prixBRange: [6, 15] },
    { objA: 'stylo', objB: 'règle', articleA: 'un stylo', articleB: 'une règle', plurA: 'stylos', plurB: 'règles', genreA: 'm', genreB: 'f', prixARange: [2, 5], prixBRange: [1, 4] },
    { objA: 'croissant', objB: 'baguette', articleA: 'un croissant', articleB: 'une baguette', plurA: 'croissants', plurB: 'baguettes', genreA: 'm', genreB: 'f', prixARange: [1, 3], prixBRange: [1, 2] },
    { objA: 'tee-shirt', objB: 'casquette', articleA: 'un tee-shirt', articleB: 'une casquette', plurA: 'tee-shirts', plurB: 'casquettes', genreA: 'm', genreB: 'f', prixARange: [5, 15], prixBRange: [3, 10] },
    { objA: 'sandwich', objB: 'jus', articleA: 'un sandwich', articleB: 'un jus', plurA: 'sandwichs', plurB: 'jus', genreA: 'm', genreB: 'm', prixARange: [3, 7], prixBRange: [1, 4] },
    { objA: 'classeur', objB: 'feutre', articleA: 'un classeur', articleB: 'un feutre', plurA: 'classeurs', plurB: 'feutres', genreA: 'm', genreB: 'm', prixARange: [3, 8], prixBRange: [1, 3] },
    { objA: 'melon', objB: 'citron', articleA: 'un melon', articleB: 'un citron', plurA: 'melons', plurB: 'citrons', genreA: 'm', genreB: 'm', prixARange: [2, 5], prixBRange: [1, 2] },
];

/**
 * Familles de combinaisons pour substitution.
 * Chaque famille = { nA1, nB1, nA2, nB2 } tels que la résolution est simple.
 */
const FAMILLES_COMBI = [
    // Famille 1 : (2A + 1B) vs (1A + 2B) → diff des totaux = A − B
    { nA1: 2, nB1: 1, nA2: 1, nB2: 2 },
    // Famille 2 : (3A + 1B) vs (1A + 1B) → diff = 2A → A = diff/2
    { nA1: 3, nB1: 1, nA2: 1, nB2: 1 },
    // Famille 3 : (2A + 1B) vs (2A + 3B) → diff = 2B → B = diff/2
    { nA1: 2, nB1: 1, nA2: 2, nB2: 3 },
    // Famille 4 : (1A + 3B) vs (1A + 1B) → diff = 2B → B = diff/2
    { nA1: 1, nB1: 3, nA2: 1, nB2: 1 },
    // Famille 5 : (3A + 2B) vs (1A + 2B) → diff = 2A → A = diff/2
    { nA1: 3, nB1: 2, nA2: 1, nB2: 2 },
];

/**
 * Génère un problème de substitution de prix valide.
 */
const genSubstitData = () => {
    const ctx = randomChoice(CTX_SUBSTIT);
    const fam = randomChoice(FAMILLES_COMBI);
    let prixA, prixB;
    let attempts = 0;
    do {
        prixA = randInt(ctx.prixARange[0], ctx.prixARange[1]);
        prixB = randInt(ctx.prixBRange[0], ctx.prixBRange[1]);
        attempts++;
    } while (prixA === prixB && attempts < 50);

    const S1 = fam.nA1 * prixA + fam.nB1 * prixB;
    const S2 = fam.nA2 * prixA + fam.nB2 * prixB;
    return { ctx, fam, prixA, prixB, S1, S2 };
};

/** Formule le nombre d'objets : "1 livre" ou "3 livres" */
const fmtObjets = (n, sing, plur) => n === 1 ? `1 ${sing}` : `${n} ${plur}`;

// --- QCM ---
const genSubstitQcm = () => {
    const data = genSubstitData();
    const { ctx, fam, prixA, prixB, S1, S2 } = data;
    const [p1, p2] = deuxPrenoms();
    const demandeA = Math.random() < 0.5;
    const reponse = demandeA ? prixA : prixB;
    const correct = `${reponse} €`;
    const objDemande = demandeA ? ctx.articleA : ctx.articleB;

    // Priorité aux distracteurs à l'échelle du PRIX (revue 2026-08-01) : avec
    // S1/diff/moyenne en tête, la bonne réponse était le min des choix dans
    // 75 % des tirages (« je coche le plus petit ») et jamais le max.
    const dSet = new Set();
    addUnique(dSet, `${demandeA ? prixB : prixA} €`, correct);       // confusion article A/B
    addUnique(dSet, `${Math.abs(S1 - S2)} €`, correct);              // oubli du ÷ 2 (souvent 2 × un prix)
    const proche = Math.random() < 0.5 ? reponse + 1 : Math.max(1, reponse - 1);
    addUnique(dSet, `${proche} €`, correct);                         // proche à ± 1, jamais systématique
    addUnique(dSet, `${S1} €`, correct);                             // secours échelle totaux
    addUnique(dSet, `${Math.round((S1 + S2) / 2)} €`, correct);
    addUnique(dSet, `${S2} €`, correct);
    let att = 0;
    while (dSet.size < 3 && att < 20) {
        addUnique(dSet, `${randInt(1, 20)} €`, correct);
        att++;
    }
    const choixList = [correct, ...[...dSet].slice(0, 3)];

    const texte = `<p>${p1.nom} achète ${fmtObjets(fam.nA1, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB1, ctx.objB, ctx.plurB)} pour ${S1} €. ${p2.nom} achète ${fmtObjets(fam.nA2, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB2, ctx.objB, ctx.plurB)} pour ${S2} €. Quel est le prix d'${objDemande} ?</p>`;

    const payload = makeQcm(choixList, correct, texte, opts(L_SUBSTIT));
    payload._meta.generatorKey = 'substitution_prix';
    return payload;
};

// --- Saisie ---
const genSubstitSaisie = () => {
    const data = genSubstitData();
    const { ctx, fam, prixA, prixB, S1, S2 } = data;
    const [p1, p2] = deuxPrenoms();
    const demandeA = Math.random() < 0.5;
    const reponse = demandeA ? prixA : prixB;
    const objDemande = demandeA ? ctx.articleA : ctx.articleB;

    const texte = `<p>${p1.nom} achète ${fmtObjets(fam.nA1, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB1, ctx.objB, ctx.plurB)} pour ${S1} €. ${p2.nom} achète ${fmtObjets(fam.nA2, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB2, ctx.objB, ctx.plurB)} pour ${S2} €. Quel est le prix d'${objDemande} ?</p>`;

    const payload = makeSaisie(String(reponse), texte, { ...opts(L_SUBSTIT), suffixe: '€' });
    payload._meta.generatorKey = 'substitution_prix';
    return payload;
};

// --- Relier ---
// Refonte bilan prof D3 « plus tard » (arbitré 2026-07-27) : l'ancien 2 ↔ 2
// se résolvait à 50 % au hasard (un prix trouvé = l'autre forcé). Gabarit
// 2 sources / 4 cibles, leurres = les productions de la « direct translation
// strategy » (Hegarty & Mayer ; Vergnaud) — l'élève combine les nombres de
// l'énoncé sans modéliser :
//   · difference_brute : |S1 − S2| — il soustrait les totaux et s'arrête là
//     (dans 4 familles sur 5 c'est l'oubli du ÷ 2 : la différence vaut 2A ou
//     2B). Collisions RÉELLES filtrées par RETIRAGE : diff = un prix (B = 2A),
//     diff = partage ((3A+B)/4 = 2A quand B = 5A) ;
//   · partage_egal : S1 ÷ (nombre d'articles du 1er achat) — « tous les
//     articles au même prix » — quand c'est entier et distinct ;
//   · repli somme_prix : A + B (le prix du lot pris pour un prix d'article) —
//     PROUVÉ sans collision : A + B = 2A ⟺ B = A (exclu), A + B > partage.
const genSubstitRelier = () => {
    let data = null;
    let diff = 0;
    for (let t = 0; t < 60 && !data; t++) {
        const cand = genSubstitData();
        const d = Math.abs(cand.S1 - cand.S2);
        if (d === cand.prixA || d === cand.prixB || d === 0) continue;
        data = cand;
        diff = d;
    }
    if (!data) {
        // Repli déterministe : famille 2 (3A+1B vs 1A+1B), A=7, B=5 →
        // S1=26, S2=12, diff=14 ∉ {7,5} ✓
        const fam = { nA1: 3, nB1: 1, nA2: 1, nB2: 1 };
        // Contexte FIGÉ sur livre/cahier (revue 2026-08-01) : 7 € et 5 € sont
        // dans leurs plages — un contexte au hasard pouvait donner « un crayon
        // à 7 € » en contradiction avec les plages de prix par objet.
        const ctx = CTX_SUBSTIT.find((c) => c.objA === 'livre');
        data = { ctx, fam, prixA: 7, prixB: 5, S1: 26, S2: 12 };
        diff = 14;
    }
    const { ctx, fam, prixA, prixB, S1, S2 } = data;
    const [p1, p2] = deuxPrenoms();

    const pairs = [
        { source: ctx.objA.charAt(0).toUpperCase() + ctx.objA.slice(1), target: `${prixA} €` },
        { source: ctx.objB.charAt(0).toUpperCase() + ctx.objB.slice(1), target: `${prixB} €` },
    ];

    const leurres = [{ target: `${diff} €`, code: 'difference_brute' }];
    // Leurre prioritaire ecart_prix (revue 2026-08-01) : |A − B|, la confusion
    // « prix » / « différence de prix ». Petit par nature, il casse la
    // heuristique « je relie aux deux plus petites cibles » (52 % de réussite
    // mesurée avec somme/partage seuls, toujours plus grands que les prix).
    // Filtré s'il vaut un prix, la différence brute, ou zéro (F1 : diff = A − B).
    const ecartPrix = Math.abs(prixA - prixB);
    if (ecartPrix > 0 && ![prixA, prixB, diff].includes(ecartPrix)) {
        leurres.push({ target: `${ecartPrix} €`, code: 'ecart_prix' });
    } else {
        const nb1 = fam.nA1 + fam.nB1;
        const partage = S1 % nb1 === 0 ? S1 / nb1 : null;
        if (partage !== null && ![prixA, prixB, diff].includes(partage)) {
            leurres.push({ target: `${partage} €`, code: 'partage_egal' });
        } else {
            leurres.push({ target: `${prixA + prixB} €`, code: 'somme_prix' });
        }
    }

    const texte = `<p>${p1.nom} achète ${fmtObjets(fam.nA1, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB1, ctx.objB, ctx.plurB)} pour ${S1} €. ${p2.nom} achète ${fmtObjets(fam.nA2, ctx.objA, ctx.plurA)} et ${fmtObjets(fam.nB2, ctx.objB, ctx.plurB)} pour ${S2} €. Relie chaque article à son prix. Attention : certains prix ne correspondent à aucun article.</p>`;

    const payload = makeRelier(pairs, texte, { ...opts(L_SUBSTIT), cibles_leurres: leurres });
    payload._meta.generatorKey = 'substitution_prix';
    payload._meta.params = {
        prixA, prixB, S1, S2,
        distracteurs: Object.fromEntries(leurres.map(l => [l.target, l.code])),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    balance_inconnue_qcm:           genBalanceQcm,
    balance_inconnue_saisie:        genBalanceSaisie,
    balance_inconnue_texte_trou:    genBalanceTexteTrou,
    repartition_ecarts_qcm:        genRepartQcm,
    repartition_ecarts_saisie:     genRepartSaisie,
    repartition_ecarts_texte_trou: genRepartTexteTrou,
    substitution_prix_qcm:         genSubstitQcm,
    substitution_prix_saisie:      genSubstitSaisie,
    substitution_prix_relier:      genSubstitRelier,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag', texte_trou: 'Texte à trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    qcm: 'QCM', saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'balance_inconnue',    label: L_BALANCE,  formatsList: buildFormats('balance_inconnue') },
    { id: 'repartition_ecarts',  label: L_REPART,   formatsList: buildFormats('repartition_ecarts') },
    { id: 'substitution_prix',   label: L_SUBSTIT,  formatsList: buildFormats('substitution_prix') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    PRENOMS, OBJETS_BALANCE, CTX_REPART, CTX_SUBSTIT, FAMILLES_COMBI,
    genBalanceData, genRepartData3, genRepartData2, genSubstitData,
    genBalanceQcm, genBalanceSaisie, genBalanceTexteTrou,
    genRepartQcm, genRepartSaisie, genRepartTexteTrou,
    genSubstitQcm, genSubstitSaisie, genSubstitRelier,
};
